import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, Download, ShieldAlert } from 'lucide-react'
import { Capacitor } from '@capacitor/core'

import {
  fetchApkUpdateInfo,
  openAgentChatApkInstaller,
  type ApkDownloadProgress,
} from '../lib/apkUpdate'
import { apkUpdateDiagnosticEventForStatus, recordApkUpdateDiagnostic } from '../lib/apkUpdateDiagnostics'
import { isAgentChatNativeApp } from '../lib/nativeExternalOpen'

type LauncherState =
  | 'idle'
  | 'opening'
  | 'opened'
  | 'external'
  | 'permission'
  | 'in_progress'
  | 'navigated'
  | 'error'

const DISMISS_KEY = 'agentchat-apk-update-dismissed'

function dismissedVersion(): number {
  try {
    return Number(window.localStorage.getItem(DISMISS_KEY)) || 0
  } catch {
    return 0
  }
}

function rememberDismissed(versionCode: number): void {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(versionCode))
  } catch {
    /* private/hardened storage — fine, we just re-offer next launch */
  }
}

function apkUpdateToken(search: string): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const direct = params.get('apk_update')
  if (direct) return direct === '0' ? null : direct === '1' ? String(Date.now()) : direct
  const nested = params.get('next')
  if (nested) {
    try { return apkUpdateToken(new URL(nested, window.location.origin).search) } catch { return null }
  }
  return null
}

function apkUpdateAutostart(search: string): boolean {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const token = params.get('apk_update')
  if (token) return params.get('apk_update_autostart') === '1'
  const nested = params.get('next')
  if (nested) {
    try { return apkUpdateAutostart(new URL(nested, window.location.origin).search) } catch { return false }
  }
  return false
}

function staleNativeApkToken(): string | null {
  if (!isAgentChatNativeApp()) return null
  if (Capacitor.isPluginAvailable('AgentChatAppInfo')) return null
  return 'stale-native'
}

function clearApkUpdateParams(): void {
  const next = new URL(window.location.href)
  next.searchParams.delete('apk_update')
  next.searchParams.delete('apk_update_autostart')
  next.searchParams.delete('agentchat_update')
  window.history.replaceState(window.history.state, '', `${next.pathname}${next.search}${next.hash}`)
}

export default function ApkUpdateLauncher() {
  const loc = useLocation()
  const startedRef = useRef<string | null>(null)
  const [token, setToken] = useState<string | null>(
    () => apkUpdateToken(window.location.search) ?? staleNativeApkToken(),
  )
  // Auto-detected newer build (no push / no Settings needed).
  const [available, setAvailable] = useState<{ code: number; name: string | null } | null>(null)
  const [state, setState] = useState<LauncherState>('idle')
  const [progress, setProgress] = useState<ApkDownloadProgress | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Push / deep-link / stale-shell triggers (unchanged).
  useEffect(() => {
    const nextToken = apkUpdateToken(loc.search) ?? staleNativeApkToken()
    if (!nextToken) return
    if (nextToken === token) return
    setToken(nextToken)
    setState('idle')
    setError(null)
    recordApkUpdateDiagnostic({ event: 'seen', token: nextToken })
  }, [loc.search, token])

  // Auto-detect: poll the published version on launch + every app-foreground,
  // and pop the install card on our own when a newer build exists. This is the
  // "I just want a pop-up, not Settings" path — no notification round-trip, no
  // navigating. Snoozes per-version so it never nags for a build you dismissed.
  useEffect(() => {
    if (!isAgentChatNativeApp()) return
    let cancelled = false
    const check = async () => {
      try {
        const info = await fetchApkUpdateInfo()
        if (cancelled) return
        if (
          info.updateAvailable &&
          info.latestVersionCode &&
          info.latestVersionCode > dismissedVersion()
        ) {
          setAvailable({ code: info.latestVersionCode, name: info.latestVersionName })
          recordApkUpdateDiagnostic({ event: 'seen', token: `available-${info.latestVersionCode}` })
        }
      } catch {
        /* offline / not built yet — stay quiet */
      }
    }
    void check()
    const onVisible = () => { if (document.visibilityState === 'visible') void check() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  // A param/stale token wins (it's an explicit ask); otherwise use the
  // auto-detected build. `cacheBust` doubles as the installer cache-buster.
  const cacheBust = token ?? (available ? `available-${available.code}` : null)

  const startInstall = useCallback(async () => {
    if (!cacheBust) return
    setState('opening')
    setError(null)
    setProgress(null)
    recordApkUpdateDiagnostic({ event: 'opening', token: cacheBust })
    clearApkUpdateParams()
    try {
      const status = await openAgentChatApkInstaller(cacheBust, setProgress)
      recordApkUpdateDiagnostic({
        event: apkUpdateDiagnosticEventForStatus(status),
        token: cacheBust,
      })
      setState(
        status === 'permission_required'
          ? 'permission'
          : status === 'in_progress'
            ? 'in_progress'
          : status === 'external_opened'
            ? 'external'
            : status === 'navigated'
              ? 'navigated'
              : 'opened',
      )
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      recordApkUpdateDiagnostic({ event: 'error', token: cacheBust, error_code: message.slice(0, 120) })
      setError(message || 'Could not start the update.')
      setState('error')
    }
  }, [cacheBust])

  // Autostart only applies to an explicit push/deep-link (apk_update_autostart=1).
  useEffect(() => {
    if (!token || !apkUpdateAutostart(loc.search) || startedRef.current === token) return
    startedRef.current = token
    const timer = window.setTimeout(() => { void startInstall() }, 300)
    return () => window.clearTimeout(timer)
  }, [loc.search, startInstall, token])

  const dismiss = () => {
    if (available) rememberDismissed(available.code)
    setAvailable(null)
    setToken(null)
    setState('idle')
    setError(null)
  }

  if (!cacheBust) return null

  return (
    <div className="fixed inset-x-3 bottom-3 z-[80] mx-auto max-w-md rounded-lg border border-stone-200 bg-white p-3 text-stone-900 shadow-xl dark:border-stone-700 dark:bg-stone-900 dark:text-stone-50">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
          {state === 'opened' ? (
            <CheckCircle2 size={18} />
          ) : state === 'permission' ? (
            <ShieldAlert size={18} />
          ) : (
            <Download size={18} />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">Update AgentChat</div>
          <div className="mt-1 text-xs leading-5 text-stone-600 dark:text-stone-300">
            {state === 'opening'
              ? progress?.percent != null
                ? `Downloading update… ${progress.percent}%`
                : 'Downloading update…'
              : state === 'opened'
                ? 'Android installer opened. Tap Install or Update there, then fully close and reopen AgentChat.'
                : state === 'external'
                  ? 'Android download opened outside AgentChat. Tap the downloaded AgentChat APK, then tap Install or Update.'
                  : state === 'navigated'
                    ? 'Download started. Open the completed AgentChat APK from notifications or Downloads, then tap Install or Update.'
                    : state === 'permission'
                      ? 'Allow AgentChat to install apps, then tap Install update again.'
                      : state === 'in_progress'
                        ? "An AgentChat update is already downloading. Wait for Android's installer, or retry in a moment."
                        : available?.name
                          ? `A newer build is ready: ${available.name}. Tap Install update.`
                          : 'Tap Install update to fetch the latest Android build.'}
          </div>
          {state === 'opening' && progress?.percent != null && (
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-stone-200 dark:bg-stone-700">
              <div
                className="h-full rounded-full bg-blue-600 transition-[width] duration-200"
                style={{ width: `${progress.percent}%` }}
              />
            </div>
          )}
          {error && (
            <div className="mt-2 flex items-start gap-1.5 text-xs leading-5 text-red-700 dark:text-red-300">
              <AlertTriangle className="mt-0.5 shrink-0" size={14} />
              {error}
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-stretch gap-1.5">
          <button
            type="button"
            onClick={() => void startInstall()}
            disabled={state === 'opening'}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md bg-blue-600 px-3 text-xs font-semibold text-white disabled:cursor-progress disabled:bg-blue-300"
          >
            <Download size={14} />
            {state === 'opened'
              ? 'Open installer'
              : state === 'in_progress'
                ? 'Retry'
              : state === 'external' || state === 'navigated'
                ? 'Open download'
                : 'Install update'}
          </button>
          {state !== 'opening' && state !== 'opened' && (
            <button
              type="button"
              onClick={dismiss}
              className="inline-flex h-7 items-center justify-center rounded-md px-3 text-[11px] font-medium text-stone-500 hover:bg-stone-100 dark:text-stone-400 dark:hover:bg-stone-800"
            >
              Later
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
